import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useEvents } from '../lib/hooks';
import { errorText } from '../lib/errors';
import { cleanFlat, fmtDateTime, isFlat, isIndianMobile, normalizePhone } from '../lib/format';
import type { FlatCheck } from '../lib/types';
import { ErrorNote, LOGO_SRC, Splash } from '../components/ui';

export const TOWERS = Array.from({ length: 11 }, (_, i) => i + 1);

/**
 * The first screen after signing in, for an email that isn't in a family yet: where do you live? A flat where a
 * family already uses the portal (or the Food Committee booked for one) asks first: join them, or be a separate family.
 */
export default function LinkFlat() {
  const { me, meLoading, refreshMe, signOut, isStaff, hasRole } = useAuth();
  const { live } = useEvents();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/';
  const [mode, setMode] = useState<'form' | 'occupied'>('form');
  const [check, setCheck] = useState<FlatCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [tower, setTower] = useState('');
  const [flat, setFlat] = useState('');

  const request = me?.request;
  // While a request waits, look now and then whether the family (or the help desk) let this person in.
  useEffect(() => {
    if (request?.status !== 'pending') return;
    const t = setInterval(() => void refreshMe(), 15_000);
    return () => clearInterval(t);
  }, [request?.status, refreshMe]);

  if (meLoading) return <Splash />;
  if (me?.flat) return <Navigate to={next} replace />;

  async function done() {
    await refreshMe();
    await Promise.all([qc.invalidateQueries({ queryKey: ['dashboard'] }), qc.invalidateQueries({ queryKey: ['history'] })]);
    navigate(next, { replace: true });
  }

  async function run(fn: () => Promise<void>) {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  function details() {
    if (name.trim().length < 2) throw new Error('name_invalid');
    const d = normalizePhone(phone);
    if (!isIndianMobile(d)) throw new Error('phone_invalid');
    if (!tower) throw new Error('tower_invalid');
    if (!isFlat(flat)) throw new Error('flat_invalid');
    return { d, t: Number(tower), f: cleanFlat(flat) };
  }

  const start = () => run(async () => {
    const { d, t, f } = details();
    const c = await api.flatCheck(t, f);
    if (c.families > 0 || c.no_email > 0) {
      setCheck(c);
      setMode('occupied');
      return;
    }
    await api.linkNewFlat(name, d, t, f);
    await done();
  });

  const separate = () => run(async () => {
    const { d, t, f } = details();
    await api.linkNewFlat(name, d, t, f);
    await done();
  });

  const askToJoin = () => run(async () => {
    const { d, t, f } = details();
    await api.joinRequest(name, d, t, f);
    await refreshMe();
    setMode('form');
  });

  const ownInstead = () => run(async () => {
    if (!request) return;
    await api.cancelRequest();
    await api.linkNewFlat(request.name ?? '', request.phone ?? '', request.tower, request.flat_no);
    await done();
  });

  const otherFlat = () => run(async () => {
    if (request) {
      setName(request.name ?? '');
      setPhone(request.phone ?? '');
    }
    await api.cancelRequest();
    await refreshMe();
    setFlat('');
    setMode('form');
  });

  const brand = <span className="brand-chip"><img src={LOGO_SRC} alt="ONE10" style={{ height: 40 }} /></span>;
  const footer = (
    <>
      {isStaff && (
        <div className="row">
          <Link className="btn ghost sm" to="/committee">Console</Link>
          {hasRole('caterer', 'committee') && <Link className="btn ghost sm" to="/counter">Scanner</Link>}
        </div>
      )}
      <button type="button" className="link-btn" onClick={() => void signOut().then(() => navigate('/signin'))}>Wrong email? Sign out</button>
    </>
  );

  if (request && mode === 'form') {
    const pending = request.status === 'pending';
    return (
      <div className="shell">
        <div className="auth">
          {brand}
          <h1>{pending ? `Waiting to join your family at ${request.flat}` : `Nobody at ${request.flat} said yes`}</h1>
          {pending ? (
            <p className="muted">
              We told the family at {request.flat}, and the help desk can see your request too (sent {fmtDateTime(request.created_at)}).
              Once someone lets you in, this page opens your meal passes. You can also ask them to do it now.
            </p>
          ) : (
            <p className="muted">
              If you live there with them, ask them to open <b>Menu → My family</b> and add your email or send you a join link.
              Otherwise start your own family: you get your own bookings and QR.
            </p>
          )}
          <ErrorNote error={error} />
          {pending && <button className="btn primary block lg" disabled={busy} onClick={() => void refreshMe()}>Check again</button>}
          <button className={`btn ${pending ? 'ghost' : 'primary'} block`} disabled={busy} onClick={() => void ownInstead()}>
            Start our own family at {request.flat}
          </button>
          <button className="link-btn" disabled={busy} onClick={() => void otherFlat()}>
            {pending ? 'Withdraw the request' : 'Wrong flat? Try another'}
          </button>
          {footer}
        </div>
      </div>
    );
  }

  if (mode === 'occupied' && check) {
    const portal = check.families > 0;
    const one = check.families === 1;
    return (
      <div className="shell">
        <div className="auth">
          <button type="button" className="link-btn" onClick={() => { setMode('form'); setError(null); }}>‹ Back</button>
          {portal ? (
            <>
              <h1>{one ? 'A family at' : `${check.families} families at`} {check.label} already {one ? 'uses' : 'use'} the portal</h1>
              <p className="muted">Are you part of {one ? 'that family' : 'one of them'}? A family books together and shares one QR for its meals.</p>
            </>
          ) : (
            <>
              <h1>The Food Committee booked for a family at {check.label}</h1>
              <p className="muted">That family hasn't added an email yet. Is it yours?</p>
            </>
          )}
          <button className="choice" disabled={busy} onClick={() => void askToJoin()}>
            <b>{portal ? 'Yes, ask them to let me in' : "Yes, that's us"}</b>
            <span>{portal
              ? "We email them your name and email. Once one of them says yes, you see the family's bookings and passes."
              : 'Ask to join it: the help desk checks with the mobile number on that booking and lets you in.'}</span>
          </button>
          <button className="choice" disabled={busy} onClick={() => void separate()}>
            <b>No, we're a separate family</b>
            <span>Your own bookings and QR. The free Bhog seats are counted per flat, so you share that limit with {portal && one ? 'them' : 'the others'}.</span>
          </button>
          <button className="choice" disabled={busy} onClick={() => { setMode('form'); setError(null); }}>
            <b>Wrong flat number?</b>
            <span>Go back and fix it.</span>
          </button>
          {portal && check.no_email > 0 && (
            <div className="notice">
              <p>The Food Committee also booked for a family at {check.label} that has no email yet. If that's yours, ask to join: the help
                desk checks with the mobile number on that booking.</p>
            </div>
          )}
          <ErrorNote error={error} />
        </div>
      </div>
    );
  }

  const booking = live.filter((e) => e.booking_open);
  return (
    <div className="shell">
      <form className="auth" onSubmit={(e) => { e.preventDefault(); void start(); }}>
        {brand}
        {booking.length > 0 && <p className="app-eyebrow">Now booking · {booking.map((e) => e.name).join(' · ')}</p>}
        <h1>Welcome to ONE10 Food Pass</h1>
        <p className="muted">You're signed in as <b>{me?.email}</b>. One last step: tell us who you are and where you live.</p>
        <ol className="wsteps" aria-label="How it works">
          <li><b>Your flat</b><span>Just this once</span></li>
          <li><b>Book meals</b><span>For your whole family</span></li>
          <li><b>One QR</b><span>Show it at the counter</span></li>
        </ol>
        <div>
          <label className="fld-l" htmlFor="name">Your name</label>
          <input id="name" className="fld" autoComplete="name" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div>
          <label className="fld-l" htmlFor="phone">Mobile number</label>
          <div className="phone-in"><span>+91</span>
            <input id="phone" inputMode="numeric" autoComplete="tel-national" placeholder="98765 43210" maxLength={14}
              value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <p className="fine">Only for the Food Committee, if they need to reach you about a payment.</p>
        </div>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label className="fld-l" htmlFor="tower">Tower</label>
            <select id="tower" className="fld" value={tower} onChange={(e) => setTower(e.target.value)}>
              <option value="">Choose</option>
              {TOWERS.map((t) => <option key={t} value={t}>Tower {t}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label className="fld-l" htmlFor="flat">Flat</label>
            <input id="flat" className="fld" maxLength={4} autoComplete="off" autoCapitalize="characters" placeholder="e.g. 3B"
              value={flat} onChange={(e) => setFlat(cleanFlat(e.target.value))} />
          </div>
        </div>
        <ErrorNote error={error} />
        <button className="btn primary block lg" type="submit" disabled={busy}>{busy ? 'Checking…' : 'Continue'}</button>
        <p className="fine">
          Your family already uses the portal? Enter the same flat and ask to join them, or ask them for a join link from
          <b> Menu → My family</b>.
        </p>
        {footer}
      </form>
    </div>
  );
}
